import { useEffect } from 'react';

const APP_TITLE = 'FlowQA';
const DEFAULT_DESCRIPTION =
  'Track test runs, surface failure patterns, and understand quality trends across suites, tests, and pipelines.';
const SITE_ORIGIN = 'https://www.flowqa.com';
const DEFAULT_IMAGE_URL = `${SITE_ORIGIN}/logo.png`;

export type PageRobotsPolicy = 'index,follow' | 'noindex,follow' | 'noindex,nofollow';

export type PageMetadataOptions = {
  canonicalPath?: string;
  robots?: PageRobotsPolicy;
};

type ResolvedPageMetadata = {
  title: string;
  description: string;
  canonicalUrl: string | null;
  robots: PageRobotsPolicy;
};

const buildPageTitle = (title?: string): string => {
  const trimmedTitle = title?.trim() ?? '';
  if (!trimmedTitle) {
    return APP_TITLE;
  }
  if (trimmedTitle === APP_TITLE || (trimmedTitle.startsWith('[') && trimmedTitle.endsWith(']'))) {
    return trimmedTitle;
  }
  return `${APP_TITLE} · ${trimmedTitle}`;
};

const resolveDescription = (description?: string): string => {
  const resolvedDescription = description?.trim();
  return resolvedDescription && resolvedDescription.length > 0
    ? resolvedDescription
    : DEFAULT_DESCRIPTION;
};

const resolveCanonicalUrl = (canonicalPath?: string): string | null => {
  const trimmedPath = canonicalPath?.trim();
  if (!trimmedPath) return null;
  return new URL(trimmedPath, SITE_ORIGIN).toString();
};

export const resolvePageMetadata = (
  title?: string,
  description?: string,
  options: PageMetadataOptions = {},
): ResolvedPageMetadata => ({
  title: buildPageTitle(title),
  description: resolveDescription(description),
  canonicalUrl: resolveCanonicalUrl(options.canonicalPath),
  robots: options.robots ?? 'noindex,nofollow',
});

export const usePageTitle = (title?: string): void => {
  useEffect(() => {
    document.title = buildPageTitle(title);
  }, [title]);
};

const getOrCreateMetaTag = (selector: string, attribute: 'name' | 'property'): HTMLMetaElement => {
  const existingTag = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${selector}"]`);
  if (existingTag) {
    return existingTag;
  }
  const tag = document.createElement('meta');
  tag.setAttribute(attribute, selector);
  document.head.appendChild(tag);
  return tag;
};

const setMetaTagContent = (
  selector: string,
  attribute: 'name' | 'property',
  content: string,
): void => {
  getOrCreateMetaTag(selector, attribute).setAttribute('content', content);
};

const removeMetaTag = (selector: string, attribute: 'name' | 'property'): void => {
  document.querySelector<HTMLMetaElement>(`meta[${attribute}="${selector}"]`)?.remove();
};

const setCanonicalLink = (canonicalUrl: string | null): void => {
  const existingLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonicalUrl) {
    existingLink?.remove();
    return;
  }
  if (existingLink) {
    existingLink.setAttribute('href', canonicalUrl);
    return;
  }
  const link = document.createElement('link');
  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', canonicalUrl);
  document.head.appendChild(link);
};

export const usePageMetadata = (
  title?: string,
  description?: string,
  options: PageMetadataOptions = {},
): void => {
  useEffect(() => {
    const resolvedMetadata = resolvePageMetadata(title, description, options);

    document.title = resolvedMetadata.title;
    setMetaTagContent('description', 'name', resolvedMetadata.description);
    setMetaTagContent('robots', 'name', resolvedMetadata.robots);
    setCanonicalLink(resolvedMetadata.canonicalUrl);

    setMetaTagContent('og:title', 'property', resolvedMetadata.title);
    setMetaTagContent('og:description', 'property', resolvedMetadata.description);
    setMetaTagContent('og:type', 'property', 'website');
    setMetaTagContent('og:image', 'property', DEFAULT_IMAGE_URL);
    setMetaTagContent('twitter:card', 'name', 'summary');
    setMetaTagContent('twitter:title', 'name', resolvedMetadata.title);
    setMetaTagContent('twitter:description', 'name', resolvedMetadata.description);
    setMetaTagContent('twitter:image', 'name', DEFAULT_IMAGE_URL);

    if (resolvedMetadata.canonicalUrl) {
      setMetaTagContent('og:url', 'property', resolvedMetadata.canonicalUrl);
    } else {
      removeMetaTag('og:url', 'property');
    }
  }, [description, options, title]);
};
